import { produce } from "immer";
import * as Actions from "pages/Settings/Sharing/store/sharingActions";

const initialState = {
  grantees: {
    isLoaded: false,
    isLoading: false,
    errorMessage: null,
    items: []
  },
  invitations: {
    isLoading: false,
    errorMessage: null,
    items: [],
    // Invitations hidden with "Yok Say" (see ignoreInvitation)
    ignored: []
  },
  shares: {
    isLoaded: false,
    isLoading: false,
    errorMessage: null,
    items: []
  },
  // Folder trees per view, used to pick what to share
  resources: {},
  // Folder whose share modal is open from a page menu (calendar, tasks...)
  shareTarget: null
};

const reducer = produce((draft, action) => {
  switch (action.type) {
    case Actions.FETCHING_GRANTEES: {
      draft.grantees.isLoading = true;
      draft.grantees.errorMessage = null;
      break;
    }

    case Actions.FETCH_GRANTEES_SUCCESS: {
      draft.grantees.isLoaded = true;
      draft.grantees.isLoading = false;
      draft.grantees.errorMessage = null;
      draft.grantees.items = action.payload;
      break;
    }

    case Actions.FETCH_GRANTEES_FAILED: {
      draft.grantees.isLoading = false;
      draft.grantees.errorMessage = action.payload;
      break;
    }

    case Actions.FETCHING_INVITATIONS: {
      draft.invitations.isLoading = true;
      draft.invitations.errorMessage = null;
      break;
    }

    case Actions.FETCH_INVITATIONS_SUCCESS: {
      draft.invitations.isLoading = false;
      draft.invitations.errorMessage = null;
      draft.invitations.items = action.payload;
      break;
    }

    case Actions.FETCH_INVITATIONS_FAILED: {
      draft.invitations.isLoading = false;
      draft.invitations.errorMessage = action.payload;
      break;
    }

    case Actions.ADD_GRANTEE_SUCCESS: {
      const { folder, grantee } = action.payload;

      if (!grantee?.zid) {
        break;
      }

      let target = draft.grantees.items.find(item => item.id === folder.id);

      if (!target) {
        target = {
          id: folder.id,
          name: folder.name,
          view: folder.view,
          grantees: []
        };
        draft.grantees.items.push(target);
      }

      target.grantees = (target.grantees || []).filter(
        item => item.zid !== grantee.zid
      );
      target.grantees.push(grantee);
      break;
    }

    case Actions.REMOVE_GRANTEE_SUCCESS: {
      const { folderId, zid } = action.payload;
      const target = draft.grantees.items.find(item => item.id === folderId);

      if (target) {
        target.grantees = (target.grantees || []).filter(
          item => item.zid !== zid
        );
      }
      break;
    }

    case Actions.FETCHING_RESOURCES: {
      // A refetch keeps showing the folders it already has meanwhile
      draft.resources[action.payload.view] = {
        isLoaded: false,
        isLoading: true,
        errorMessage: null,
        items: draft.resources[action.payload.view]?.items || []
      };
      break;
    }

    case Actions.FETCH_RESOURCES_SUCCESS: {
      draft.resources[action.payload.view] = {
        isLoaded: true,
        isLoading: false,
        errorMessage: null,
        items: action.payload.items
      };
      break;
    }

    case Actions.FETCH_RESOURCES_FAILED: {
      draft.resources[action.payload.view] = {
        isLoaded: false,
        isLoading: false,
        errorMessage: action.payload.errorMessage,
        items: []
      };
      break;
    }

    case Actions.RESOURCES_CHANGED: {
      // Kept on screen until the refetch replaces it
      if (draft.resources[action.payload.view]) {
        draft.resources[action.payload.view].isLoaded = false;
      }
      break;
    }

    case Actions.FETCHING_SHARES: {
      draft.shares.isLoading = true;
      draft.shares.errorMessage = null;
      break;
    }

    case Actions.FETCH_SHARES_SUCCESS: {
      draft.shares.isLoaded = true;
      draft.shares.isLoading = false;
      draft.shares.errorMessage = null;
      draft.shares.items = action.payload;
      break;
    }

    case Actions.FETCH_SHARES_FAILED: {
      draft.shares.isLoading = false;
      draft.shares.errorMessage = action.payload;
      break;
    }

    case Actions.REMOVE_SHARE_SUCCESS: {
      const { id, view } = action.payload;

      draft.shares.items = draft.shares.items.filter(item => item.id !== id);
      // The folder tree of that view still holds the mountpoint: mark it stale
      // (removeShare refetches it). Dropping it left the sidebar without the
      // user's own folders until a reload
      if (draft.resources[view]) {
        draft.resources[view].isLoaded = false;
      }
      break;
    }

    case Actions.SET_IGNORED_INVITATIONS: {
      draft.invitations.ignored = action.payload;
      break;
    }

    case Actions.ACCEPT_INVITATION_SUCCESS: {
      const key = Actions.invitationKey(action.payload);

      draft.invitations.items = draft.invitations.items.filter(
        item => Actions.invitationKey(item) !== key
      );
      // The tree of that view now holds the new mountpoint: mark it stale
      // (acceptInvitation refetches it)
      if (draft.resources[action.payload.view]) {
        draft.resources[action.payload.view].isLoaded = false;
      }
      break;
    }

    case Actions.OPEN_SHARE_MODAL: {
      draft.shareTarget = action.payload.folder;
      break;
    }

    case Actions.CLOSE_SHARE_MODAL: {
      draft.shareTarget = null;
      break;
    }

    default:
      return draft;
  }
}, initialState);

export default reducer;
